import { checklistAnswerValue, hasAnswerObservation } from '@/services/checklist/checklistObservations';
import { hasMediaCancellation, sameMediaRow } from './ChecklistMediaConflictContract';

type Row = Record<string, any>;

export interface ChecklistMediaCanonicalAdoptionInput {
  operation: Row;
  pending: Row;
  value: Row;
  remote: Row;
  remoteValues: readonly Row[];
  parent: Row;
  snapshot: Row;
  field: Row;
  publicUrl: string;
  canCommit: () => boolean;
}

export interface ChecklistMediaCanonicalAdoptionRows {
  operation?: Row | null;
  pending?: Row | null;
  value?: Row | null;
  destination?: Row | null;
  parent?: Row | null;
  snapshot?: Row | null;
  field?: Row | null;
  fields: Row[];
  values: Row[];
  images: Row[];
  operations: Row[];
  conflicts: Row[];
  archives: Row[];
  tombstones: Row[];
}

const mediaType = (type: unknown): boolean => typeof type === 'string' &&
  ['foto', 'photo', 'marcacao', 'mark', 'assinatura', 'signature'].includes(
    type.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase());

const references = (value: unknown, ids: readonly string[]): boolean => {
  if (typeof value === 'string') {
    if (ids.includes(value)) return true;
    try { return references(JSON.parse(value), ids); } catch { return false; }
  }
  if (Array.isArray(value)) return value.some(item => references(item, ids));
  return Boolean(value && typeof value === 'object' && Object.entries(value).some(([key, item]) =>
    ids.includes(key) || references(item, ids)));
};

/** Adota somente a identidade da primeira imagem cuja nota foi criada independentemente na web. */
export function planChecklistMediaCanonicalAdoption(input: ChecklistMediaCanonicalAdoptionInput,
  rows: ChecklistMediaCanonicalAdoptionRows): { valueChanges: Row; pendingChanges: Row } | null {
  const { value, pending, remote, parent, snapshot, field, operation } = input;
  if (!input.canCommit() || !sameMediaRow(rows.operation, operation) || !sameMediaRow(rows.pending, pending) ||
      !sameMediaRow(rows.value, value) || !sameMediaRow(rows.parent, parent) ||
      !sameMediaRow(rows.snapshot, snapshot) || !sameMediaRow(rows.field, field) || rows.destination) return null;
  const companyId = parent.empresa_id;
  const originalId = field.original_campo_id;
  if (!companyId || parent.retired_at || parent.id !== value.checklist_id || parent.id !== pending.item_id ||
      parent.snapshot_id !== snapshot.id || snapshot.checklist_id !== parent.id || snapshot.empresa_id !== companyId ||
      field.snapshot_id !== snapshot.id || field.empresa_id !== companyId || !originalId || !mediaType(field.tipo) ||
      value.empresa_id !== companyId || value.snapshot_field_id !== field.id || value.campo_id !== originalId ||
      remote.empresa_id !== companyId || remote.checklist_id !== parent.id || remote.snapshot_field_id !== field.id ||
      (remote.campo_id !== null && remote.campo_id !== originalId) || !value.id || !remote.id || value.id === remote.id ||
      remote.valor !== null || typeof remote.observacao !== 'string' || !remote.observacao ||
      typeof remote.updated_at !== 'string' || !Number.isFinite(Date.parse(remote.updated_at)) ||
      (value.observacao ?? null) !== null || pending.baseline_captured !== 1 || pending.expected_remote_value !== null ||
      pending.expected_remote_updated_at !== null) return null;
  const physicalFields = rows.fields.filter(row => row.id === originalId);
  if (physicalFields.length > 1 || physicalFields.some(row => row.empresa_id !== companyId ||
      row.template_id !== parent.template_id || !mediaType(row.tipo))) return null;
  if (pending.item_type !== 'CHECKLIST' || pending.empresa_id !== companyId || pending.campo_id !== field.id ||
      !pending.user_id || operation.user_id !== pending.user_id || operation.empresa_id !== companyId ||
      operation.operation !== 'checklist_partial_update' || operation.collection !== 'guincho_valores_checklist' ||
      operation.item_id !== parent.id || !pending.local_uri || !pending.bucket || !pending.path ||
      typeof input.publicUrl !== 'string' || !/^https?:\/\//i.test(input.publicUrl) ||
      pending.prepared_public_url !== input.publicUrl || value.valor !== input.publicUrl) return null;
  if (typeof pending.local_uri !== 'string' || !(/^(file|content|blob|temp|data):/i.test(pending.local_uri) ||
      /^checklist-(photos|signatures|marcacoes|marcacao-bkg)\//i.test(pending.local_uri)) ||
      !['checklists-photos', 'checklists-marcacoes', 'checklists-signatures'].some(folder =>
        pending.path.startsWith(`${folder}/${companyId}/${parent.id}-`)) ||
      pending.path.split('/').includes('..')) return null;
  const aliases = [field.id, originalId];
  const logical = (row: Row): boolean => aliases.includes(row.snapshot_field_id) || aliases.includes(row.campo_id);
  const locals = rows.values.filter(logical);
  const remotes = input.remoteValues.filter(logical);
  const images = rows.images.filter(row => aliases.includes(row.campo_id));
  if (locals.length !== 1 || locals[0].id !== value.id || remotes.length !== 1 ||
      !sameMediaRow(remotes[0], remote) || images.length !== 1 || images[0].id !== pending.id) return null;
  let answers: Row;
  try { answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; } catch { return null; }
  if (!answers || typeof answers !== 'object' || Array.isArray(answers) ||
      !Object.prototype.hasOwnProperty.call(answers, field.id) || hasAnswerObservation(answers[field.id]) ||
      checklistAnswerValue(answers[field.id]) !== input.publicUrl || Object.prototype.hasOwnProperty.call(answers, originalId)) return null;
  const dependencies = [value.id, remote.id, pending.id, ...aliases];
  const deletionTargets = [...dependencies, parent.id, parent.template_id, snapshot.id];
  if (rows.tombstones.some(row => row.empresa_id === companyId && deletionTargets.includes(row.entity_id)) ||
      rows.operations.some(row => row.operation === 'delete' && deletionTargets.includes(row.item_id)) ||
      hasMediaCancellation(rows.conflicts, pending)) return null;
  for (const row of rows.operations) {
    if (row.item_id === value.id || row.item_id === remote.id || row.item_id === pending.id) return null;
    if (row.operation !== 'checklist_partial_update' || row.item_id !== parent.id) {
      if (references(row.data, dependencies)) return null;
      continue;
    }
    let data: Row;
    try { data = typeof row.data === 'string' ? JSON.parse(row.data) : row.data; } catch { return null; }
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    for (const alias of aliases) {
      if (!Object.prototype.hasOwnProperty.call(data, alias)) continue;
      if (row.id !== operation.id || hasAnswerObservation(data[alias]) ||
          checklistAnswerValue(data[alias]) !== input.publicUrl) return null;
    }
  }
  if ([...rows.conflicts, ...rows.archives].some(row => references(row, dependencies))) return null;
  // Os tempos locais permanecem: a imagem ainda é uma intenção pendente, não um ACK remoto.
  return { valueChanges: { id: remote.id, campo_id: remote.campo_id, observacao: remote.observacao },
    pendingChanges: { baseline_captured: 1, expected_remote_value: null, expected_remote_updated_at: remote.updated_at } };
}
